<script setup>
// A soft, cloud-shaped content card for the spiritual section.
// Public API is unchanged: <CloudCard tone="primary|accent"> ... </CloudCard>
defineProps({
  tone: { type: String, default: 'primary' } // 'primary' | 'accent'
});
</script>

<template>
  <div class="cloud-card" :class="`cloud-card--${tone}`">
    <!-- puffs are pure decoration; they share the card's fill colour so the
         overlap with the body is seamless and the drop-shadow wraps the lot -->
    <span class="cloud-card__puffs cloud-card__puffs--top" aria-hidden="true">
      <i></i><i></i><i></i>
    </span>

    <div class="cloud-card__body">
      <slot />
    </div>

    <span class="cloud-card__puffs cloud-card__puffs--bottom" aria-hidden="true">
      <i></i><i></i>
    </span>
  </div>
</template>

<style scoped>
.cloud-card {
  --cloud-fill: #ffffff;
  --cloud-shadow: rgba(84, 68, 96, 0.18);

  position: relative;
  /* headroom so the puffs never collide with neighbouring content */
  margin-block: clamp(30px, 5vw, 42px);
  padding: clamp(22px, 3vw, 32px);
  background: var(--cloud-fill);
  border-radius: clamp(26px, 4vw, 40px);
  /* one soft shadow around the whole composed silhouette (body + puffs) */
  filter:
    drop-shadow(0 16px 22px var(--cloud-shadow))
    drop-shadow(0 5px 9px rgba(84, 68, 96, 0.08));
  animation: cloud-bob 9s ease-in-out infinite;
}

.cloud-card--primary { --cloud-fill: color-mix(in srgb, var(--brand) 6%, #fff); }
.cloud-card--accent {
  --cloud-fill: color-mix(in srgb, var(--accent) 7%, #fff);
  animation-delay: -3.5s; /* de-sync the bob when two sit side by side */
}

/* ---- puffs -------------------------------------------------------------- */
.cloud-card__puffs {
  position: absolute;
  left: 0;
  right: 0;
  pointer-events: none;
}
.cloud-card__puffs--top { top: 0; }
.cloud-card__puffs--bottom { bottom: 0; }

.cloud-card__puffs i {
  position: absolute;
  background: var(--cloud-fill);
  border-radius: 50%; /* wider than tall -> reads as a soft cloud bump */
}

.cloud-card__puffs--top i { top: 0; height: clamp(46px, 8vw, 78px); }
.cloud-card__puffs--top i:nth-child(1) { width: 40%; left: 2%;   transform: translateY(-44%); }
.cloud-card__puffs--top i:nth-child(2) { width: 46%; left: 28%;  transform: translateY(-52%); }
.cloud-card__puffs--top i:nth-child(3) { width: 34%; right: 2%;  transform: translateY(-40%); }

.cloud-card__puffs--bottom i { bottom: 0; height: clamp(38px, 6vw, 60px); }
.cloud-card__puffs--bottom i:nth-child(1) { width: 38%; left: 8%;  transform: translateY(46%); }
.cloud-card__puffs--bottom i:nth-child(2) { width: 30%; right: 10%; transform: translateY(42%); }

/* ---- body ------------------------------------------------------------- */
.cloud-card__body {
  position: relative; /* keep text above every puff */
  z-index: 1;
  display: grid;
  gap: 8px;
}
.cloud-card__body :deep(h3) { margin: 0; }
.cloud-card__body :deep(p:last-child) { margin-bottom: 0; }

@keyframes cloud-bob {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-6px); }
}

@media (prefers-reduced-motion: reduce) {
  .cloud-card { animation: none; }
}
</style>
